/**
 * Safe links (SR-01, SR-13): every URL that comes from entity or task data
 * goes through these helpers before it reaches a `Link`.
 */

// ASCII control characters and DEL. Browsers drop some of them while parsing
// a URL, so "java\tscript:" or "/\n/host" could turn into something else.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001F\u007F]/;

/**
 * The text without its trailing "/" characters. A loop, not `/\/+$/`: that
 * regex backtracks on a long run of "/" that is not at the end.
 */
export function trimTrailingSlashes(text: string): string {
  let end = text.length;
  while (end > 0 && text[end - 1] === '/') {
    end -= 1;
  }
  return text.slice(0, end);
}

/**
 * True for a path inside this app: it starts with a single "/". "//host" and
 * "/\host" are protocol-relative URLs to another origin, so they are not.
 */
export function isAppPath(href: unknown): href is string {
  return (
    typeof href === 'string' &&
    /^\/(?![/\\])/.test(href) &&
    !CONTROL_CHARS.test(href)
  );
}

/**
 * Returns a link target that is safe to render, or `undefined`.
 *
 * Accepts absolute `http:`/`https:` URLs and app paths that start with a
 * single "/". A leading `url:` (catalog location refs) is stripped. Anything
 * else (`javascript:`, `data:`, `vbscript:`, "//host", relative paths) is
 * dropped. Never throws.
 */
export function toSafeHref(raw?: unknown): string | undefined {
  if (typeof raw !== 'string') {
    return undefined;
  }
  const value = raw.startsWith('url:') ? raw.slice('url:'.length) : raw;
  if (isAppPath(value)) {
    return value;
  }
  try {
    // No base URL: only absolute URLs parse.
    const url = new URL(value);
    if (url.protocol === 'http:' || url.protocol === 'https:') {
      return url.href;
    }
  } catch {
    // Not an absolute URL.
  }
  return undefined;
}

/**
 * One plain e-mail address: `local@domain`, with no whitespace, control
 * characters or the characters that would add mail headers, more
 * recipients or a fragment to a `mailto:` link (`?`, `&`, `,`, `%`, `;`,
 * `<`, `>`, `#`).
 */
const PLAIN_EMAIL =
  /^[^\s@?&,%;:<>/\\"#]+@[^\s@?&,%;:<>/\\"#]+\.[^\s@?&,%;:<>/\\"#.]+$/;

/**
 * Control characters for an address: the ASCII ones and DEL, plus the
 * Unicode C1 controls U+0080–U+009F (S3-1). Kept apart from
 * `CONTROL_CHARS`, so `toSafeHref` and `isAppPath` are unchanged.
 */
// eslint-disable-next-line no-control-regex
const ADDRESS_CONTROL_CHARS = /[\u0000-\u001F\u007F-\u009F]/;

/**
 * A `mailto:` link to one plain address (a squad's Contato), or `undefined`
 * when the value is anything else. Kept apart from `toSafeHref`, which
 * rejects `mailto:` on purpose. Never throws.
 */
export function toMailtoHref(email?: unknown): string | undefined {
  if (typeof email !== 'string') {
    return undefined;
  }
  const value = email.trim();
  if (ADDRESS_CONTROL_CHARS.test(value) || !PLAIN_EMAIL.test(value)) {
    return undefined;
  }
  return `mailto:${value}`;
}
